import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';
import { Employee, getEmployeeName } from '../../config';

// Signature placement geometry -- the shared constants every surface that draws
// a signature uses. The mock below reproduces the block at 1:1 print size in
// the document font, so the placement chosen here is exactly what the editor,
// the PDF and the signing dialog render.
import { SIGNATURE_BASE_HEIGHT_PT, SIGNATURE_LINE_MM } from '../../../Documents/config';

export interface SignaturePlacementValue {
  signature_scale: number;
  signature_offset_x_mm: number;
  signature_offset_y_mm: number;
}

interface SignaturePlacementProps {
  isOpen: boolean;
  employee: Employee;
  value: SignaturePlacementValue;
  /** Printed under the name in the mock, as they will be on a document. */
  positionTitle: string;
  registrationNumber: string;
  onChange: (patch: Partial<SignaturePlacementValue>) => void;
  onSignatureChanged: () => void;
}

const clampOffset = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.round(value * 10) / 10));

/**
 * The signature image over a 1:1 mock of the document's signature block:
 * upload, drag into place, scale. The placement is part of the modal's draft
 * and saved with it; the image itself is stored the moment it uploads.
 */
const SignaturePlacement: React.FC<SignaturePlacementProps> = ({
  isOpen,
  employee,
  value,
  positionTitle,
  registrationNumber,
  onChange,
  onSignatureChanged
}) => {
  const supabase = useSupabaseClient();

  // Signature image state. The path lives on team_members and is written as soon
  // as the upload succeeds, rather than on form submit, so the preview and the
  // stored value can never disagree.
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);

  // Drag-to-position state for the signature placement mock. The ref carries
  // the drag's fixed context (start point, starting offsets, px-per-mm) so
  // pointermove only does arithmetic; the boolean just switches the cursor.
  const [isDraggingSignature, setIsDraggingSignature] = useState(false);
  const signatureDragRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    pxPerMm: number;
  } | null>(null);
  const signatureLineRef = useRef<HTMLDivElement>(null);

  // Load a preview of the stored signature. The bucket is private, so this is a
  // short-lived signed URL rather than a public link.
  useEffect(() => {
    let cancelled = false;
    const path = employee.signature_storage_path;

    if (!isOpen || !path) {
      setSignatureUrl(null);
      return;
    }

    supabase.storage
      .from('signatures')
      .createSignedUrl(path, 3600)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Error creating signature preview URL:', error);
          setSignatureUrl(null);
          return;
        }
        setSignatureUrl(data?.signedUrl ?? null);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, employee.signature_storage_path, supabase]);

  useEffect(() => {
    setSignatureError(null);
    setIsUploadingSignature(false);
  }, [isOpen, employee.id]);

  const handleSignatureUpload = useCallback(async (file: File) => {
    setSignatureError(null);

    // PNG only, and deliberately NOT routed through the Documents module's
    // compressImage helper: that re-encodes to JPEG, which flattens the
    // transparency a signature needs and leaves an opaque rectangle sitting on
    // top of the signature rule in the document.
    if (file.type !== 'image/png') {
      setSignatureError('Signature must be a PNG with a transparent background.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setSignatureError('Signature image must be under 2MB.');
      return;
    }

    setIsUploadingSignature(true);
    try {
      // WRITE-ONCE: every upload gets a fresh uuid and superseded files are left
      // in place. document_signatures snapshots this path at signing time, so
      // overwriting a file would retroactively change the signature on every
      // document already issued.
      const path = `${employee.id}/${crypto.randomUUID()}.png`;

      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(path, file, { contentType: 'image/png' });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('team_members')
        .update({ signature_storage_path: path })
        .eq('id', employee.id);

      if (updateError) throw updateError;

      onSignatureChanged();
    } catch (err) {
      console.error('Error uploading signature:', err);
      notifyReadOnly(err);
      setSignatureError('Could not upload the signature. Please try again.');
    } finally {
      setIsUploadingSignature(false);
    }
  }, [employee.id, supabase, onSignatureChanged]);

  // Drag the signature image around the placement mock. px-per-mm is measured
  // off the 62mm signature line at drag start, so the math stays correct under
  // browser zoom instead of trusting CSS's nominal 96dpi.
  const handleSignaturePointerDown = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
    e.preventDefault();
    const line = signatureLineRef.current;
    const pxPerMm = line
      ? line.getBoundingClientRect().width / SIGNATURE_LINE_MM
      : 96 / 25.4;
    signatureDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: value.signature_offset_x_mm,
      baseY: value.signature_offset_y_mm,
      pxPerMm
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDraggingSignature(true);
  }, [value.signature_offset_x_mm, value.signature_offset_y_mm]);

  const handleSignaturePointerMove = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
    const drag = signatureDragRef.current;
    if (!drag) return;
    const dxMm = (e.clientX - drag.startX) / drag.pxPerMm;
    const dyMm = (e.clientY - drag.startY) / drag.pxPerMm;
    onChange({
      // Generous bounds: left of the line start through past its end, and far
      // enough down to overlap the rule and the name like a wet signature.
      signature_offset_x_mm: clampOffset(drag.baseX + dxMm, -10, 55),
      signature_offset_y_mm: clampOffset(drag.baseY + dyMm, -12, 14)
    });
  }, [onChange]);

  const handleSignaturePointerUp = useCallback(() => {
    signatureDragRef.current = null;
    setIsDraggingSignature(false);
  }, []);

  return (
    <div className="signature-placement">
      {/* A 1:1 mock of the document's signature block, in the
          document font at print size. The image is absolutely
          positioned over it exactly as the editor and PDF will
          place it, so dragging here is WYSIWYG. */}
      <div className="signature-placement__page">
        <div className="signature-placement__closing">Yours faithfully,</div>
        <div className="signature-placement__closing">For Terpkos Engineering Pty Ltd</div>
        <div className="signature-placement__space">
          {signatureUrl ? (
            <img
              src={signatureUrl}
              alt={`${getEmployeeName(employee)} signature`}
              className="signature-placement__img"
              draggable={false}
              onPointerDown={handleSignaturePointerDown}
              onPointerMove={handleSignaturePointerMove}
              onPointerUp={handleSignaturePointerUp}
              onPointerCancel={handleSignaturePointerUp}
              // Inline because the modal's global form styles set
              // img { width: 100% }, and the geometry is dynamic
              // anyway. Same mm/pt values the document renderers
              // use.
              style={{
                position: 'absolute',
                left: `${value.signature_offset_x_mm}mm`,
                bottom: `${-value.signature_offset_y_mm}mm`,
                height: `${SIGNATURE_BASE_HEIGHT_PT * value.signature_scale}pt`,
                width: 'auto',
                maxWidth: 'none',
                zIndex: 1,
                cursor: isDraggingSignature ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
              }}
            />
          ) : (
            <span className="signature-placement__empty">
              The signature will sit here
            </span>
          )}
        </div>
        <div className="signature-placement__line" ref={signatureLineRef} />
        <div className="signature-placement__name">{getEmployeeName(employee)}</div>
        {positionTitle && (
          <div className="signature-placement__meta">{positionTitle}</div>
        )}
        {registrationNumber && (
          <div className="signature-placement__meta">{registrationNumber}</div>
        )}
      </div>

      {signatureUrl && (
        <div className="signature-placement__toolbar">
          <label htmlFor="signature-scale-input">Size</label>
          <input
            id="signature-scale-input"
            type="range"
            min="0.5"
            max="2.5"
            step="0.05"
            value={value.signature_scale}
            onChange={(e) => onChange({ signature_scale: Number(e.target.value) })}
          />
          <span className="signature-placement__scale-value">
            {Math.round(value.signature_scale * 100)}%
          </span>
          <span className="signature-placement__toolbar-divider" />
          <button
            type="button"
            className="link-btn"
            onClick={() => onChange({
              signature_scale: 1,
              signature_offset_x_mm: 0,
              signature_offset_y_mm: 0
            })}
          >
            Reset position
          </button>
        </div>
      )}

      <div className="signature-placement__upload">
        <input
          type="file"
          accept="image/png"
          id="signature-file-input"
          className="signature-placement__file"
          disabled={isUploadingSignature}
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset so re-picking the same file still fires onChange.
            e.target.value = '';
            if (file) handleSignatureUpload(file);
          }}
        />
        <label htmlFor="signature-file-input" className="pill-btn pill-btn--sm">
          {isUploadingSignature
            ? 'Uploading…'
            : signatureUrl ? 'Replace signature' : 'Upload signature'}
        </label>
        <span className="signature-placement__hint">
          {signatureUrl
            ? 'Drag the signature into place — it stamps onto documents exactly as shown. PNG, transparent, under 2MB.'
            : 'PNG with a transparent background, under 2MB.'}
        </span>
      </div>

      {signatureError && (
        <p className="field__error" role="alert">{signatureError}</p>
      )}
    </div>
  );
};

export default SignaturePlacement;
